import { useCallback } from 'react';
import { TriangleAlert } from 'lucide-react';
import styles from './DynamicFormBuilder.module.css';

// Ported from app/views/dynamic_forms/_form.html.haml (the legacy edit page
// "Cheat Sheet"), plus the desc-list value-summary pattern. Keep these snippets
// in sync if the legacy help changes.

const DEFAULT_TEMPLATE = `<div class="dynamic-form">
  <div>
    <label>Value label</label>
    <div>
      {{variable}}
    </div>
  </div>
  ...
</div>`;

const DESC_LIST = `<div class="l-inline-pad">
  <dl class="desc-list desc-list--bo-renderer">
    <div class="desc-list__wrap">
      {{#if field_one}}
        <dt>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#40bf46" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
          <span>Field One</span>
        </dt>
        <dd>Description shown when field_one is set.</dd>
      {{else}}
        <dt>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
          <span>Field One</span>
        </dt>
        <dd>Description shown when field_one is not set.</dd>
      {{/if}}
    </div>
    <div class="desc-list__wrap">
      {{#if field_two}}
        <dt>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#40bf46" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
          <span>Field Two</span>
        </dt>
        <dd>Description shown when field_two is set.</dd>
      {{else}}
        <dt>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
          <span>Field Two</span>
        </dt>
        <dd>Description shown when field_two is not set.</dd>
      {{/if}}
    </div>
  </dl>
</div>`;

const CHEM_IMAGER = `<div
  data-controller="chemimager"
  data-chemimager-moleblock-value="{{chemDoodle_field_1}}"
  ></div>`;

const ICONS = `{{{ lucideIcon "folder-open" }}}
{{{ lucideIcon "download" }}}
{{{ lucideIcon "flask-conical" }}}`;


function CopyButton({ text }) {
  const copy = useCallback(() => {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
  }, [text]);
  return (
    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copy}>
      Copy
    </button>
  );
}

// A collapsible cheat-sheet section with an optional copyable example.
function Section({ title, example, defaultOpen = false, children }) {
  return (
    <details className="list-group-item" open={defaultOpen}>
      <summary className="fw-semibold">{title}</summary>
      <div className="mt-2">
        {children}
        {example && (
          <div className="d-flex flex-column gap-1 mt-1">
            <div className="d-flex justify-content-between align-items-baseline">
              <span className="text-uppercase text-muted small">Example</span>
              <CopyButton text={example} />
            </div>
            <pre className="bg-light border rounded p-2 mb-0"><code>{example}</code></pre>
          </div>
        )}
      </div>
    </details>
  );
}

export default function RendererHelpPanel() {
  return (
    <aside className={`card p-0 border-info rounded ${styles.cheatSheetPanel}`} aria-label="Renderer cheat sheet">
      <div className="card-header bg-info">Cheat Sheet</div>
      <div className="list-group list-group-flush list-group-clean">
        <Section title="Default Template" example={DEFAULT_TEMPLATE} defaultOpen>
          <p>
            Use the following HTML structure in your renderer to enable the default <code>.dynamic-form</code> styling.
            You can also override the <code>.dynamic-form</code> class and/or specify your own custom markup.
          </p>
        </Section>

        <Section title="Definition List" example={DESC_LIST}>
          <p>
            To list form values as labelled items, use a <code>{'<dl class="desc-list desc-list--bo-renderer">'}</code> with
            one <code>{'<div class="desc-list__wrap">'}</code> per item. Each item pairs a <code>{'<dt>'}</code> (a 
            <code>{'<svg>'}</code> icon plus a label <code>{'<span>'}</code>) with a <code>{'<dd>'}</code>
            describing the value. Wrap each item in <code>{'{{#if}}'}</code>/<code>{'{{else}}'}</code> so it adapts to the
            submitted data — give the icon an inline green <code>stroke</code> colour (e.g. <code>#40bf46</code>) in the
            affirmative branch and leave it default (<code>currentColor</code>) in the negative branch.
          </p>
        </Section>

        <Section title="Chemical Molecule Editor" example={CHEM_IMAGER}>
          <p>
            If you are adding a <code>Chemical Molecule Editor (chemDoodle)</code>, use the following HTML structure
            in your renderer to create a <code>Chemical Molecule Imager (chemImager)</code> that shows the built
            molecule. Replace <code>chemDoodleId</code> with the number at the end of your Chemical Editor field,
            found in the Schema or Options tabs. Note: This is only set up to work with dynamic forms attached to wares.
          </p>
        </Section>

        <Section title="Using Icons" example={ICONS}>
          <p>
            If you are adding icons we are using the{' '}
            <a href="https://lucide.dev/icons/" target="_blank" rel="noreferrer">Lucide</a>{' '}
            icon library package. You need to add a <code>lucideIcon</code> Handlebars helper with triple braces
            (required so the SVG is not escaped) and pass in the <strong>icon name</strong> with quotes.
          </p>
          <p>This renders the icon directly as part of the page with no extra setup.</p>
          <p>
            All icon names are lowercase with hyphens.{' '}
            <a href="https://github.com/scientist-hq/rx/blob/main/docs/claude-resources/lucide-icons.md#icon-names" target="_blank" rel="noreferrer">Here</a>{' '}
            are commonly used icons on the platform:
          </p>
          <p className="mb-0 text-warning d-flex align-items-start gap-1">
            <TriangleAlert size={16} strokeWidth={2} aria-hidden className="flex-shrink-0 mt-1" />
            Icons are opt-in and not all ~2000 Lucide icons are bundled. If an icon is needed and not rendering,
            it may not be in the project yet. Contact the engineering team to add an icon.
          </p>
        </Section>

        <Section title="PDF Rendering">
          <p className="mb-0">
            PDFs only support a subset of HTML elements. Click{' '}
            <a href="https://github.com/blocknotes/prawn-html#supported-tags--attributes" target="_blank" rel="noreferrer">here</a>{' '}
            to view the list of available tags.
          </p>
        </Section>
      </div>
    </aside>
  );
}
